import { useEffect, useState } from "react";
import "./styles/Legend_style.css";
import NDVILegend from "./NDVILegend";
import CombinedHazardLegend from "./CombinedHazardLegend";
import CombinedForecastLegend from "./CombinedForecastLegend";
import { base_url} from "./Geoportal";

const Legend = ({
  spi_legend_url,
  precipitation_check,
  fapar_anomaly_check,
  fapar_anormaly_legend_url,
  fapar_absorbed_check,
  fapar_absorbed_legend_url,
  monthly_precipitation_check,
  gpcc_monthly_precipitation_legend_url,
  sma_check,
  legend_url_sma,
  cdi_check,
  ndvi_check,
  koppen_climate_clas_check,
  koppen_climate_url,
  unwdc_check,
  checked_spi_era5,
  cdi_era5_check,
  longrange_forecast_check,
  drought_persistence_check,
  combined_hazard_era5_check,
  combined_hazard_gpcc_check,
  combined_forecast_era5_check,
  combined_forecast_gpcc_check,
  layerOpacity,
  onOpacityChange,
}) => {
  const fapar_url =
    `${base_url}/geoserver/wms?REQUEST=GetLegendGraphic&VERSION=1.0.0&FORMAT=image/png&WIDTH=20&HEIGHT=20&LAYER=africa_drought_datasets:grid_083dd_fapar_anomaly_0101_view&LEGEND_OPTIONS=border:true;dx:10;fontSize`;

  const cdi_url =
    `${base_url}/geoserver/wms?REQUEST=GetLegendGraphic&VERSION=1.0.0&FORMAT=image/png&WIDTH=20&HEIGHT=20&LAYER=africa_drought_datasets:grid_01dd_cdi_0501_view&LEGEND_OPTIONS=border:true;dx:10;fontSize`;
  const unwdc_url =
    `${base_url}/geoserver/wms?REQUEST=GetLegendGraphic&VERSION=1.0.0&FORMAT=image/png&WIDTH=20&HEIGHT=20&LAYER=africa_drought_datasets:grid_1dd_unwdc_0901_view&LEGEND_OPTIONS=border:true;dx:10;fontSize`;

  const spi_era5_url = `${base_url}/geoserver/wms?REQUEST=GetLegendGraphic&VERSION=1.0.0&FORMAT=image/png&WIDTH=20&HEIGHT=20&LAYER=africa_drought_datasets:grid_025dd_spi_0801_03_view&LEGEND_OPTIONS=border:true;dx:10;fontSize`;
  const drought_persistence_url = `${base_url}/geoserver/wms?REQUEST=GetLegendGraphic&VERSION=1.0.0&FORMAT=image/png&WIDTH=20&HEIGHT=20&LAYER=africa_drought_datasets:grid_01dd_drought_to_persist_cdi_0111_view&LEGEND_OPTIONS=border:true;dx:10;fontSize`;

  const longrange_forecast_url = `${base_url}/geoserver/wms?REQUEST=GetLegendGraphic&VERSION=1.0.0&FORMAT=image/png&WIDTH=20&HEIGHT=20&LAYER=africa_drought_datasets:data_api_forecastpolygon&LEGEND_OPTIONS=border:true;dx:10;fontSize`;

  const [legendItems, setLegendItems] = useState([]);

  useEffect(() => {
    const updatedLegendItems = [];

    if (cdi_url !== null) {
      updatedLegendItems.unshift({
        url: cdi_url,
        name: "Combined Drought Index (CDI)",
        check: cdi_check || cdi_era5_check,
        layerOpacityKey: "cdi",
      });
    }

    if (unwdc_url !== null) {
      updatedLegendItems.unshift({
        url: unwdc_url,
        name: "UNWDC",
        check: unwdc_check,
        layerOpacityKey: "unwdc",
      });
    }

    if (spi_legend_url !== null) {
      updatedLegendItems.unshift({
        url: spi_legend_url,
        name: "Standard Precipitation Index (SPI)",
        check: precipitation_check,
        layerOpacityKey: "spi",
      });
    }

    if (fapar_anormaly_legend_url !== null) {
      updatedLegendItems.unshift({
        url: fapar_url,
        name: "FAPAR Anomaly",
        check: fapar_anomaly_check,
        layerOpacityKey: "faparAnom",
      });
    }

    if (fapar_absorbed_legend_url !== null) {
      updatedLegendItems.unshift({
        url: fapar_absorbed_legend_url,
        name: "FAPAR Absorbed",
        check: fapar_absorbed_check,
        layerOpacityKey: "faparAbsorbed",
      });
    }

    if (gpcc_monthly_precipitation_legend_url !== null) {
      updatedLegendItems.unshift({
        url: gpcc_monthly_precipitation_legend_url,
        name: "Monthly Precipitation GPCC",
        check: monthly_precipitation_check,
        layerOpacityKey: "monthly_precipitation_gpcc",
      });
    }

    if (legend_url_sma !== null) {
      updatedLegendItems.unshift({
        url: legend_url_sma,
        name: "Soil Moisture Anomaly",
        check: sma_check,
        layerOpacityKey: "sma",
      });
    }

    if (koppen_climate_url !== null) {
      updatedLegendItems.unshift({
        url: koppen_climate_url?.leged_url,
        name: "Koppen Climate Classification",
        check: koppen_climate_clas_check,
        layerOpacityKey: "koppen_climate",
      });
    }

    if (spi_era5_url !== null) {
      updatedLegendItems.unshift({
        url: spi_era5_url,
        name: "SPI Era5",
        check: checked_spi_era5,
        layerOpacityKey: "spi_era5",
      });
    }

    if (drought_persistence_url !== null) {
      updatedLegendItems.unshift({
        url: drought_persistence_url,
        name: "Drought Persistence",
        check: drought_persistence_check,
        layerOpacityKey: "drought_persistence",
      });
    }

    if (longrange_forecast_url !== null) {
      updatedLegendItems.unshift({
        url: longrange_forecast_url,
        name: "Long-range Forecast",
        check: longrange_forecast_check,
        layerOpacityKey: "longrange_forecast",
      });
    }

    setLegendItems(updatedLegendItems);
  }, [
    spi_legend_url,
    fapar_anormaly_legend_url,
    fapar_absorbed_legend_url,
    gpcc_monthly_precipitation_legend_url,
    legend_url_sma,
    precipitation_check,
    fapar_anomaly_check,
    fapar_absorbed_check,
    monthly_precipitation_check,
    sma_check,
    cdi_check,
    koppen_climate_clas_check,
    koppen_climate_url,
    unwdc_check,
    checked_spi_era5,
    cdi_era5_check,
    longrange_forecast_check,
    drought_persistence_check
  ]);

  const OpacitySlider = ({ opacityKey }) => {
    const val = layerOpacity?.[opacityKey] ?? 1;
    return (
      <div className="layer-opacity-control">
        <span className="layer-opacity-label-text">Opacity</span>
        <input
          type="range"
          className="layer-opacity-slider"
          min="0"
          max="1"
          step="0.05"
          value={val}
          onChange={(e) => onOpacityChange?.(opacityKey, parseFloat(e.target.value))}
        />
        <span className="layer-opacity-label">{Math.round(val * 100)}%</span>
      </div>
    );
  };

  return (
    <>
      {legendItems.map((legendItem) => {
        const { url, name, check, layerOpacityKey } = legendItem;
        if (check) {
          return (
            <div key={name}>
              <h4 style={{ marginBottom: "2px" }}>{name}</h4>
              <img src={url} alt="legend" />
              <OpacitySlider opacityKey={layerOpacityKey} />
            </div>
          );
        }
        return null;
      })}
      {ndvi_check && (
        <div>
          <NDVILegend />
          <OpacitySlider opacityKey="ndvi" />
        </div>
      )}
      {combined_hazard_era5_check && (
        <CombinedHazardLegend title="Combined Monitoring & Forecast Hazard (ERA5)" />
      )}
      {combined_hazard_gpcc_check && (
        <CombinedHazardLegend title="Combined Monitoring & Forecast Hazard (GPCC)" />
      )}
      {combined_forecast_era5_check && (
        <CombinedForecastLegend title="Combined Drought Forecast (ERA5)" />
      )}
      {combined_forecast_gpcc_check && (
        <CombinedForecastLegend title="Combined Drought Forecast (GPCC)" />
      )}
    </>
  );
};

export default Legend;
